import { Button, NavItem, NavLink, Overlay, Popover, PopoverBody } from "@bankmonitor/bm-ui-kit";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AccountAnalysisService from "@service/analysis/AccountAnalysisService";
import { prefersReducedMotion } from "@util/motion";
import { hasSeenHint, markHintSeen } from "@util/oneTimeHint";

const HINT_KEY = "bm-analysis-nav-hint-seen";
const ANALYSIS_PATH = "/szamla-elemzeseim";
const SHOW_DELAY_MS = 500;
const MOBILE_QUERY = "(max-width: 991.98px)";

interface Props {
	active: boolean;
	toggleRef?: RefObject<HTMLButtonElement>;
	onSelect?: () => void;
}

function matchesMobile(): boolean {
	return window.matchMedia?.(MOBILE_QUERY)?.matches ?? false;
}

function useIsMobile(): boolean {
	const [isMobile, setIsMobile] = useState(matchesMobile);

	useEffect(() => {
		const mql = window.matchMedia?.(MOBILE_QUERY);
		if (!mql) {
			return;
		}
		const onChange = () => setIsMobile(mql.matches);
		onChange();
		mql.addEventListener?.("change", onChange);
		return () => mql.removeEventListener?.("change", onChange);
	}, []);

	return isMobile;
}

export function AnalysisNavHint({ active, toggleRef, onSelect }: Props): ReactNode {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const navLinkRef = useRef<HTMLAnchorElement>(null);
	const [show, setShow] = useState(false);
	const isMobile = useIsMobile();

	const isAnalysisPath = pathname.startsWith(ANALYSIS_PATH);

	const hide = useCallback(() => setShow(false), []);

	const markSeenAndHide = useCallback(() => {
		markHintSeen(HINT_KEY);
		setShow(false);
	}, []);

	useEffect(() => {
		const eligible = AccountAnalysisService.isEnabled() && !hasSeenHint(HINT_KEY) && !isAnalysisPath;
		if (!eligible) {
			return;
		}

		const timer = setTimeout(() => setShow(true), prefersReducedMotion() ? 0 : SHOW_DELAY_MS);
		return () => clearTimeout(timer);
	}, [isAnalysisPath]);

	useEffect(() => {
		if (isAnalysisPath) {
			setShow(false);
		}
	}, [isAnalysisPath]);

	const handleOpen = useCallback(() => {
		markSeenAndHide();
		navigate(ANALYSIS_PATH);
	}, [markSeenAndHide, navigate]);

	return (
		<>
			<NavItem>
				<NavLink
					ref={navLinkRef}
					as={Link}
					to={ANALYSIS_PATH}
					active={active}
					onClick={() => {
						markSeenAndHide();
						onSelect?.();
					}}
				>
					<FontAwesomeIcon icon={solid("chart-column")} className="me-2 text-secondary" />
					Elemzéseim
				</NavLink>
			</NavItem>
			<Overlay
				target={() => (isMobile ? (toggleRef?.current ?? navLinkRef.current) : navLinkRef.current)}
				show={show}
				placement={isMobile ? "bottom-end" : "bottom"}
				rootClose
				onHide={hide}
				transition={prefersReducedMotion() ? false : undefined}
			>
				{(overlayProps) => (
					<Popover {...overlayProps} id="analysis-nav-hint-popover" style={{ ...overlayProps.style, maxWidth: "300px" }}>
						<PopoverBody style={{ padding: "16px" }}>
							<div className="fw-bold text-dark" style={{ fontSize: "15px", marginBottom: "6px" }}>
								Új - Számlamonitor szolgáltatás!
							</div>
							<p className="text-muted mb-0" style={{ fontSize: "13.5px", lineHeight: 1.5 }}>
								Itt kérhetsz új bankszámla elemzést, adhatsz új banki felhatalmazást és megtekintheted a korábbi elemzéseidet is.
							</p>
							<div className="d-flex align-items-center" style={{ gap: "10px", marginTop: "14px" }}>
								<Button
									variant="primary"
									onClick={handleOpen}
									icon={<FontAwesomeIcon icon={solid("arrow-right")} />}
									iconPosition="end"
									style={{ fontSize: "13.5px", fontWeight: 700 }}
								>
									Megnyitom
								</Button>
								<Button variant="light" onClick={markSeenAndHide} style={{ fontSize: "13.5px", fontWeight: 600 }}>
									Rendben
								</Button>
							</div>
						</PopoverBody>
					</Popover>
				)}
			</Overlay>
		</>
	);
}
